import { useMemo } from 'react';
import { supabase } from '../lib/supabase';
import type { PilotLanguageEndorsement } from '../types/pilot';
import type { PilotTarget } from './usePilotRatings';

export interface CreateLanguageEndorsementInput {
  target: PilotTarget;
  language_code: string;
  language_proficiency_level: 1 | 2 | 3 | 4 | 5 | 6;
  language_valid_until?: string | null;
  language_assessing_body?: string | null;
}

export type UpdateLanguageEndorsementInput = Partial<
  Omit<CreateLanguageEndorsementInput, 'target'>
>;

/**
 * Data access for pilot ICAO language proficiency endorsements (migration 166).
 *
 * The returned object is memoised so callers can safely put it in
 * `useEffect` / `useCallback` dependency arrays without triggering loops.
 */
export function usePilotLanguageEndorsements() {
  return useMemo(() => {
  async function list(target: PilotTarget): Promise<PilotLanguageEndorsement[]> {
    const query = supabase.from('pilot_language_endorsements').select('*');
    const filtered =
      target.kind === 'user'
        ? query.eq('user_id', target.userId)
        : query.eq('org_pilot_id', target.orgPilotId);

    const { data, error } = await filtered
      .order('language_valid_until', { ascending: true, nullsFirst: false });

    if (error) throw error;
    return (data ?? []) as PilotLanguageEndorsement[];
  }

  async function create(
    input: CreateLanguageEndorsementInput,
  ): Promise<PilotLanguageEndorsement> {
    const row = {
      user_id: input.target.kind === 'user' ? input.target.userId : null,
      org_pilot_id: input.target.kind === 'org_pilot' ? input.target.orgPilotId : null,
      language_code: input.language_code,
      language_proficiency_level: input.language_proficiency_level,
      language_valid_until: input.language_valid_until ?? null,
      language_assessing_body: input.language_assessing_body ?? null,
    };

    const { data, error } = await supabase
      .from('pilot_language_endorsements')
      .insert(row)
      .select('*')
      .single();

    if (error) throw error;
    return data as PilotLanguageEndorsement;
  }

  async function update(
    id: string,
    input: UpdateLanguageEndorsementInput,
  ): Promise<PilotLanguageEndorsement> {
    const { data, error } = await supabase
      .from('pilot_language_endorsements')
      .update(input)
      .eq('id', id)
      .select('*')
      .single();

    if (error) throw error;
    return data as PilotLanguageEndorsement;
  }

  async function remove(id: string): Promise<void> {
    const { error } = await supabase
      .from('pilot_language_endorsements')
      .delete()
      .eq('id', id);
    if (error) throw error;
  }

    return { list, create, update, remove };
  }, []);
}
